<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useVModel } from '@vueuse/core'
import { DxPopup, DxToolbarItem } from 'devextreme-vue/popup'
import { useDefaultDialogTitle } from '@/composables/data-grid'
import { getI18n } from '@/plugins/i18n'

const emit = defineEmits(['update:visible', 'save', 'cancel', 'hidden'])
const props = defineProps({
  visible: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object,
    default: null
  },
})

const popupVisible = useVModel(props, 'visible', emit)
const options = computed(() => props.options)
const i18n = getI18n()

const onSave = () => {
  if (!options.value?.saveButton?.disabled) {
    emit('save')
  }
}

const onCancel = () => {
  if (!options.value?.cancelButton?.disabled) {
    emit('cancel')
  }
}

const useDialogTitle = () => {
  const { defaultActionTitle } = useDefaultDialogTitle()

  const dialogTitle = (i, title) => {
    return `${defaultActionTitle(i)} ${title}`
  }

  return { dialogTitle }
}

const { dialogTitle } = useDialogTitle()

const lastKeyPress = ref({ key: '', time: 0 })

const handleKeyDown = (e) => {
  if (e.key === 'Escape' || e.key === 'Enter') {
    if (e.defaultPrevented) {
      lastKeyPress.value = { key: '', time: 0 }
      return
    }

    e.stopImmediatePropagation()
    e.preventDefault()

    const now = Date.now()
    const timeDiff = now - lastKeyPress.value.time

    if (e.key === lastKeyPress.value.key && timeDiff < 500) {
      if (e.key === 'Escape') onCancel()
      if (e.key === 'Enter') onSave()
    }

    lastKeyPress.value = { key: e.key, time: now }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <dx-popup
    v-model:visible="popupVisible"
    :drag-enabled="true"
    :show-title="true"
    :hide-on-outside-click="false"
    :show-close-button="options?.showCloseButton || false"
    :close-on-esc="false"
    :height="options?.height || 'auto'"
    :max-width="options?.maxWidth"
    :max-height="options?.maxHeight"
    :width="options?.width"
    :title="dialogTitle(options?.item, options?.title)"
    @hiding="emit('hidden')"
  >
    <slot />

    <dx-toolbar-item
      v-if="!options?.saveButton?.hide"
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{
        text: options?.saveButton?.text || i18n.t('common.save'),
        icon: options?.saveButton?.icon,
        onClick: onSave
      }"
    />
    <dx-toolbar-item
      v-if="!options?.cancelButton?.hide"
      widget="dxButton"
      toolbar="bottom"
      location="after"
      :options="{
        text: options?.cancelButton?.text || i18n.t('common.cancel'),
        onClick: onCancel
      }"
    />
  </dx-popup>
</template>

<style>
</style>
